/**
 * The confidence gate every detector sits behind (D-059 PS3-1, D-060(5)/(7)).
 *
 * One shared implementation because the rule must not vary by detector: a reading is acted on only
 * when the landmarks it depends on are visible enough; below that it is uncertainty, and after
 * `trackingLossPauseMs` without a usable reading the body is lost. Six copies of that would be six
 * chances for one detector to quietly answer a question it could not see.
 *
 * The loss clock runs from the **last confident reading**, not from the first bad frame: a child
 * who half-disappears behind a sofa for a moment is a child the detector is unsure about, and the
 * question "how long since we could actually see them" is the one worth answering.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { GameplayTunables } from '../../../shared/types/tunables';
import type { DetectorOutcome } from '../detector-types';
import { landmarkConfidence, primaryPose } from '../pose-geometry';
import type { PoseFrame, PoseObservation } from '../pose-types';

export type GateResult =
  /** The landmarks are visible enough to act on. */
  | { kind: 'ready'; pose: PoseObservation; confidence: number; at: number }
  /** Nothing may be acted on; this is what the detector must return instead. */
  | { kind: 'blocked'; outcome: DetectorOutcome; lost: boolean };

export interface TrackingGateOptions {
  /** The landmarks this detector reads; confidence is their mean visibility. */
  landmarks: readonly number[];
  tunables?: GameplayTunables | undefined;
}

export interface TrackingGate {
  read(frame: PoseFrame): GateResult;
  reset(): void;
}

export function createTrackingGate(options: TrackingGateOptions): TrackingGate {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const { minCoreConfidence, trackingLossPauseMs } = tunables.pose;

  /** When the body was last seen well enough to act on, or when we first lost sight of it. */
  let since: number | null = null;
  let lossReported = false;

  return {
    read(frame: PoseFrame): GateResult {
      const pose = primaryPose(frame);
      const confidence = pose === null ? 0 : landmarkConfidence(pose, options.landmarks).mean;

      if (pose !== null && confidence >= minCoreConfidence) {
        since = frame.detectedAt;
        lossReported = false;
        return { kind: 'ready', pose, confidence, at: frame.detectedAt };
      }

      if (since === null) since = frame.detectedAt;

      if (!lossReported && frame.detectedAt - since >= trackingLossPauseMs) {
        lossReported = true;
        return {
          kind: 'blocked',
          lost: true,
          outcome: { kind: 'lost', reason: pose === null ? 'NO_POSE' : 'LOW_CONFIDENCE' },
        };
      }

      if (lossReported || pose === null) {
        // Either the loss has already been reported, or there is no body to be unsure about: an
        // empty room is presence's business, not a low-confidence reading of anybody.
        return { kind: 'blocked', lost: false, outcome: { kind: 'none' } };
      }

      return {
        kind: 'blocked',
        lost: false,
        outcome: { kind: 'uncertain', confidence, reason: 'LOW_CONFIDENCE' },
      };
    },

    reset(): void {
      since = null;
      lossReported = false;
    },
  };
}
